import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  { label: '首页', to: '/' },
  { label: '影视分类', to: '/category' },
  { label: '影视资讯', to: '/news' },
  { label: '关于我们', to: '/about' },
];

interface NavbarProps {
  overlay?: boolean;
}

export default function Navbar({ overlay = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !overlay || scrolled || menuOpen;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/category?q=${encodeURIComponent(q)}` : '/category');
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px] gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-film-line text-lg"></i>
            </span>
            <span
              className={`font-heading font-bold text-base md:text-lg whitespace-nowrap transition-colors ${
                solid ? 'text-foreground-950' : 'text-white'
              }`}
            >
              美国式禁忌4
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  isActive(link.to)
                    ? solid
                      ? 'text-primary-600 font-semibold bg-primary-50'
                      : 'text-white font-semibold bg-white/15'
                    : solid
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                      : 'text-white/85 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form
              onSubmit={handleSearch}
              className={`hidden md:flex items-center gap-2 rounded-full pl-3 pr-1 py-1 border transition-colors ${
                solid ? 'border-background-300 bg-background-100' : 'border-white/30 bg-white/10'
              }`}
            >
              <i
                className={`ri-search-line text-sm ${solid ? 'text-foreground-500' : 'text-white/80'}`}
              ></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视作品"
                className={`w-28 lg:w-40 bg-transparent text-sm outline-none ${
                  solid ? 'text-foreground-900 placeholder:text-foreground-400' : 'text-white placeholder:text-white/60'
                }`}
              />
              <button
                type="submit"
                className="w-7 h-7 rounded-full bg-accent-500 text-white flex items-center justify-center hover:bg-accent-600 transition-colors cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </form>

            <button
              onClick={() => setMenuOpen((v) => !v)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                solid ? 'text-foreground-900 hover:bg-background-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="菜单"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 px-4 py-3 animate-fade-up">
          <form
            onSubmit={handleSearch}
            className="flex items-center gap-2 rounded-full px-3 py-1.5 border border-background-300 bg-background-100 mb-3"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视作品"
              className="flex-1 bg-transparent text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
            />
          </form>
          <nav className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-3 text-sm rounded-md transition-colors ${
                  isActive(link.to)
                    ? 'text-primary-600 font-semibold bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}